import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { finalize, startWith, Subject, switchMap } from 'rxjs';
import { countdownFrom } from '@app/shared/util/helpers';
import { HttpService, MimeType } from '@technocom/common/http';
import { Endpoints } from '@app/shared/util/endpoints';
import { PHONE_REGEX } from '@app/shared/util/validators';
import { TextBoxComponent } from '@progress/kendo-angular-inputs';
import { RouterService } from '@technocom/core';
import { DEFAULT_ERRORS } from '@app/shared/constants/form-errors';
import { HttpHeaders } from '@angular/common/http';

@Component({
  selector: 'app-recovery-page',
  templateUrl: './recovery-page.component.html',
  styleUrls: ['./recovery-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecoveryPageComponent implements OnInit {
  @ViewChild('passwordbox') public passwordbox: TextBoxComponent;

  form: UntypedFormGroup;
  formCodeShowed: boolean;
  fetching: boolean;
  defaultErrors = DEFAULT_ERRORS;

  readonly resend$ = new Subject<void>();
  countdown$;

  ngOnInit() {
    this.form = this.generateForm();
  }

  get localizeBasePath(): string {
    return 'profile.recovery';
  }

  get isGetCodeBtnDisabled(): boolean {
    return this.form.get('login').invalid;
  }

  get isSendBtnDisabled(): boolean {
    return this.form.get('code').invalid || this.form.get('password').invalid;
  }
  protected httpService = inject(HttpService);
  protected fb = inject(UntypedFormBuilder);
  protected changeDetector = inject(ChangeDetectorRef);
  protected router = inject(RouterService);

  getErrorOfField(fieldName: string, errorCode: string): any {
    return this.form.get(fieldName).getError(errorCode);
  }

  showCheckFormCode(): void {
    if (this.isGetCodeBtnDisabled) {
      return;
    }

    this.countdown$ = this.resend$.pipe(
      startWith(0),
      switchMap(() => countdownFrom()),
    );

    this.httpService
      .tryPost({
        url: Endpoints.ofRegistration('send-code'),
        params: { login: this.form.get('login').value },
      })
      .pipe(switchMap(() => this.countdown$))
      .subscribe(() => {
        this.formCodeShowed = true;
        this.changeDetector?.markForCheck();
        if (this.passwordbox) {
          this.passwordbox.input.nativeElement.type = 'password';
        }
      });
  }

  recovery(): void {
    if (this.isSendBtnDisabled || this.fetching) {
      return;
    }

    this.fetching = true;

    const body = {
      login: this.form.get('login').value,
      password: this.form.get('password').value,
    };

    const headers = new HttpHeaders().set('Content-Type', MimeType.APPLICATION_JSON);

    this.httpService
      .tryPost(Endpoints.ofRegistration('recovery'), body, { params: { code: this.form.get('code').value }, headers })
      .pipe(finalize(() => (this.fetching = false)))
      .subscribe(() => {
        this.goToLoginPage();
      });
  }

  goToLoginPage() {
    void this.router.goTo('login/local', { replaceUrl: true });
  }

  private generateForm() {
    const form = this.fb.group({
      login: [null, [Validators.pattern(PHONE_REGEX), Validators.required]],
      code: [null, [Validators.required, Validators.pattern('[0-9]{4}')]],
      password: [null, [Validators.required, Validators.minLength(8)]],
    });

    return form;
  }

  // смена видимости ввода пароля
  public toggleVisibility(): void {
    const inputEl = this.passwordbox.input.nativeElement;

    if (inputEl.type === 'password') {
      inputEl.type = 'text';
    } else {
      inputEl.type = 'password';
    }
  }
}
